import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { ColorObj as PdfColorObj, Point as PdfPoint } from "@Types/pdf";
import _ from "lodash";
import type { ViewerSetting } from "@ServerTypes/accounts/accounts.schema";
import type { MeasurementItem, ReferenceMeasurement } from "@ServerTypes/measurements/measurements.schema";
import type { ViewRecord } from "@ServerTypes/views/views.schema";
import type { CompareMode } from "@WebTypes/pdf";
import {
  getDynamicDocsForNodes,
  replaceDynamicDocsForNodes,
  type DynamicRow,
} from "@Services/utils/modelPropertiesDataDB";
import { DOCUMENT_TYPE } from "@Helpers/index";
import type { AppDispatch } from "@Reducers/index";
import type { PortalUnits } from "@Actions/Viewer";

export interface ModalEntry {
  show?: boolean | undefined;
  flagForAnimation?: boolean | undefined;
  /** Modal sub-view navigation (e.g. Takeoffs panel: "Takeoffs" | "Takeoff_Detail"). */
  currentStage?: string | undefined;
  /** Sub-view to return to when navigating back. */
  backStage?: string | undefined;
  /** Sub-view shown when the modal first opens. */
  defaultStage?: string | undefined;
}

type HoopsModalState = Record<string, ModalEntry>;

const hoopsModalSlice = createSlice({
  name: "HoopsModalStatus",
  initialState: {} as HoopsModalState,
  reducers: {
    setShowOnlyModal: (state: HoopsModalState, action: PayloadAction<string | string[]>) => {
      const keysToShow = Array.isArray(action.payload) ? action.payload : [action.payload];

      Object.keys(state).forEach((key: string) => {
        if (!keysToShow.includes(key) && key !== "Viewer" && state[key]) {
          state[key]!.show = false;
        }
      });

      keysToShow.forEach((key: string) => {
        if (!Object.hasOwn(state, key)) {
          state[key] = {};
        }
        state[key]!.show = true;
      });
    },
    setModalStatus: (_state: HoopsModalState, action: PayloadAction<HoopsModalState>) => action.payload,
    setAnimationForIssueCreation: (state: HoopsModalState, action: PayloadAction<boolean>) => ({
      ...state,
      Issues: {
        ...state.Issues,
        flagForAnimation: action.payload,
      },
    }),
  },
});

const sheetActiveSlice = createSlice({
  name: "isSheetActive",
  initialState: false,
  reducers: {
    setIsSheetActive: (_state: boolean, action: PayloadAction<boolean>) => action.payload,
  },
});

const faceSelectionSlice = createSlice({
  name: "faceSelection",
  initialState: false,
  reducers: {
    setFaceSelection: (_state: boolean, action: PayloadAction<boolean>) => action.payload,
  },
});

const socketDisconnectedSlice = createSlice({
  name: "socketDisconnected",
  initialState: false,
  reducers: {
    setSocketDisconnected: (_state: boolean, action: PayloadAction<boolean>) => action.payload,
  },
});

type ModelPropertiesState = {
  /**
   * Counter bumped whenever the IndexedDB-backed `modelProperties` data
   * changes. Components can subscribe to this
   * to re-render. The actual data lives in IndexedDB, not Redux.
   */
  dataVersion: number;
  isLoading: boolean;
  isLoaded: boolean;
  isError: boolean;
  /**
   * True when the load finished with nothing to load because every file was
   * restricted or deleted. Distinguishes "you may not view these" from "this model
   * genuinely has no properties" — both otherwise look like isLoaded with no rows.
   */
  isRestricted?: boolean;
  // Additional dynamic fields (e.g., isFileFormTemplatesLoading, formTemplates, etc.)
  isFileFormTemplatesLoading?: boolean;
  isFileFormTemplatesLoaded?: boolean;
  // `object`, not a keyed record: these hold whole form-template documents (named server types),
  // which are assignable to `object` but not to `Record<string, unknown>`.
  defaultTemplate?: object | null;
  formTemplates?: object | null;
  questionIdAndNameMapper?: Record<string, { question: string; type: string }>;
};

const defaultModelProperties: ModelPropertiesState = {
  dataVersion: 0,
  isLoading: false,
  isLoaded: false,
  isError: false,
  isRestricted: false,
};

interface ModifyMetadataUnitsPayload {
  fileVersionId: string;
  unitsNew: Record<string, unknown>;
  portalUnits: PortalUnits | null | undefined;
}

interface UpdateCustomMetadataPayload {
  fileVersionId: string;
  nodeId: number[];
  fieldsData: { _id: string; value: string | number | boolean | string[] | null };
  selectedFormTemplate: string;
  /** The viewer slice types `engineType` as optional, so callers really do pass `undefined`. */
  engineType?: string | undefined;
}

interface UpdateQuantityAndUnitPayload {
  fileVersionId: string;
  nodeId: number;
  unit?: string | number | null;
  quantity?: string | number | null;
}

interface AssetDataItem {
  fileVersionId: string;
  nodeId: number;
}

interface UpdateAssetDataPayload {
  property: string;
  data: AssetDataItem[];
  action: "add" | "remove" | string;
}

const modelPropertiesSlice = createSlice({
  name: "modelProperties",
  initialState: defaultModelProperties,
  reducers: {
    /**
     * Sets flags/templates on the slice. The `data` field is *not* stored in
     * Redux anymore. Pass `bumpVersion: true` to combine the status change
     * with a `dataVersion` bump in a single dispatch (avoids double re-render
     * on initial load). For data-only mutations use `bumpDataVersion()`.
     */
    setModelProperties: (
      state: ModelPropertiesState,
      action: PayloadAction<Partial<Omit<ModelPropertiesState, "dataVersion">> & { bumpVersion?: boolean }>,
    ) => {
      const { bumpVersion, ...rest } = action.payload;
      Object.assign(state, rest);
      if (bumpVersion) state.dataVersion += 1;
    },
    bumpDataVersion: (state: ModelPropertiesState) => {
      state.dataVersion += 1;
    },
  },
});

/**
 * Mutation thunks. All four target the static/dynamic IndexedDB split:
 *   - `modifyMetadataUnits` is the only static-side write — it rewrites `properties` and
 *     `typeProperties` on every static row of the file.
 *   - The other three are dynamic-side writes scoped to a (fileVersionId, nodeId): they upsert a single
 *     `DOCUMENT_TYPE_*` doc per node and leave the other docs intact.
 *       updateCustomMetadataInModelProperties → DOCUMENT_TYPE_CUSTOM_PROPERTIES
 *       updateQuantityAndUnit                 → DOCUMENT_TYPE_QUANTITY
 *       updateAssetDataInModelProperties      → DOCUMENT_TYPE_ASSETDATA
 *
 * Each thunk dispatches `bumpDataVersion()` exactly once at the end so multi-file / multi-node operations
 * produce a single re-render.
 */

export function modifyMetadataUnits(_payload: ModifyMetadataUnitsPayload) {
  // The thunk keeps its `Promise<void>` return so it stays interchangeable with the three awaited
  // thunks below and so its `void dispatch(...)` call site stays a `void` on a promise. Nothing
  // inside suspends, so the promise is produced explicitly rather than by an `async` that never
  // awaits.
  return (dispatch: AppDispatch): Promise<void> => {
    // No IDB writes — the new units take effect on the next render because
    // consumers source `unitsNew` / `portalUnits` from elsewhere and pass
    // them into `mergeNode` via `MergeOptions`. This thunk just bumps
    // `dataVersion` so consumers re-render with the latest values.
    dispatch(bumpDataVersion());
    return Promise.resolve();
  };
}

/**
 * `DynamicRow.data` is the heterogeneous Mongo payload, typed `unknown`. This is the single place
 * that decides whether it can be treated as a key/value bag — mirrors `aggregateDynamicDocs` in
 * `@Services/utils/modelPropertiesDataDB`, which is where these rows are read back.
 */
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

export function updateCustomMetadataInModelProperties(payload: UpdateCustomMetadataPayload) {
  return async (dispatch: AppDispatch) => {
    const { fileVersionId, nodeId, fieldsData, selectedFormTemplate } = payload;
    const isEmptyArray = Array.isArray(fieldsData.value) && fieldsData.value.length === 0;

    // Batched: read all nodes' docs in one txn, then write all in one txn
    // (was one read + one write transaction per node).
    const existingList = await getDynamicDocsForNodes(nodeId.map((node) => ({ fileVersionId, nodeId: node })));

    const entries = nodeId.map((node, i) => {
      const existing = existingList[i] ?? [];
      const others = existing.filter((d) => d.DOCUMENT_TYPE !== DOCUMENT_TYPE.DOCUMENT_TYPE_CUSTOM_PROPERTIES);
      const currentCustom = existing.find((d) => d.DOCUMENT_TYPE === DOCUMENT_TYPE.DOCUMENT_TYPE_CUSTOM_PROPERTIES);

      // `DynamicRow.data` is the heterogeneous Mongo payload; for a CUSTOM_PROPERTIES row it is
      // the `{ customProperties, selectedFormTemplate }` object written a few lines below.
      const currentData: unknown = currentCustom?.data;
      const customProperties: Record<string, unknown> = {
        ...(isRecord(currentData) && isRecord(currentData.customProperties) ? currentData.customProperties : {}),
      };
      if (isEmptyArray) {
        delete customProperties[fieldsData._id];
      } else {
        customProperties[fieldsData._id] = fieldsData.value;
      }

      const nextDoc: DynamicRow = {
        fileVersionId,
        nodeId: node,
        DOCUMENT_TYPE: DOCUMENT_TYPE.DOCUMENT_TYPE_CUSTOM_PROPERTIES,
        data: {
          customProperties,
          selectedFormTemplate: { _id: selectedFormTemplate },
        },
      };

      return { fileVersionId, nodeId: node, docs: [...others, nextDoc] };
    });

    await replaceDynamicDocsForNodes(entries);
    dispatch(bumpDataVersion());
  };
}

/**
 * Updates Quantity / Unit for one node or a batch of nodes. Accepts either
 * a single payload or an array. `dataVersion` is bumped exactly once after
 * all items are processed so the UI re-renders once for the whole batch.
 */
export function updateQuantityAndUnit(payload: UpdateQuantityAndUnitPayload | UpdateQuantityAndUnitPayload[]) {
  return async (dispatch: AppDispatch) => {
    const items = Array.isArray(payload) ? payload : [payload];
    if (items.length === 0) return;

    // Batched: single read txn for all nodes, single write txn for all nodes
    // (was one read + one write transaction per node).
    const existingList = await getDynamicDocsForNodes(
      items.map(({ fileVersionId, nodeId }) => ({ fileVersionId, nodeId })),
    );

    const entries = items.map(({ fileVersionId, nodeId, unit, quantity }, i) => {
      const existing = existingList[i] ?? [];
      const others = existing.filter((d) => d.DOCUMENT_TYPE !== DOCUMENT_TYPE.DOCUMENT_TYPE_QUANTITY);
      const qtyDocs = existing.filter((d) => d.DOCUMENT_TYPE === DOCUMENT_TYPE.DOCUMENT_TYPE_QUANTITY);

      // Aggregate any prior QUANTITY rows the same way `aggregateDynamicDocs`
      // would on read — spread their `.data` together so we don't lose fields.
      let qtyData: Record<string, unknown> = {};
      for (const d of qtyDocs) {
        if (isRecord(d.data)) qtyData = { ...qtyData, ...d.data };
      }

      qtyData = {
        ...qtyData,
        Unit: unit ?? qtyData.Unit ?? null,
        Quantity: quantity ?? qtyData.Quantity ?? null,
      };

      const nextDoc: DynamicRow = {
        fileVersionId,
        nodeId,
        DOCUMENT_TYPE: DOCUMENT_TYPE.DOCUMENT_TYPE_QUANTITY,
        data: qtyData,
      };

      return { fileVersionId, nodeId, docs: [...others, nextDoc] };
    });

    await replaceDynamicDocsForNodes(entries);
    dispatch(bumpDataVersion());
  };
}

export function updateAssetDataInModelProperties(payload: UpdateAssetDataPayload) {
  return async (dispatch: AppDispatch) => {
    const { property, data, action } = payload;

    // Batched: single read txn + single write txn for all nodes
    // (was one read + one write transaction per node).
    const existingList = await getDynamicDocsForNodes(
      data.map(({ fileVersionId, nodeId }) => ({ fileVersionId, nodeId })),
    );

    const entries = data.map(({ fileVersionId, nodeId }, i) => {
      const existing = existingList[i] ?? [];
      const others = existing.filter((d) => d.DOCUMENT_TYPE !== DOCUMENT_TYPE.DOCUMENT_TYPE_ASSETDATA);
      const assetDoc = existing.find((d) => d.DOCUMENT_TYPE === DOCUMENT_TYPE.DOCUMENT_TYPE_ASSETDATA);

      // `DynamicRow.data` is the heterogeneous Mongo payload; for an ASSETDATA row it is the
      // asset-property list written a few lines below, which is `string[]` — this thunk is its only
      // writer. Filtered rather than spread so the copy stays typed; nothing but strings has ever
      // been put in the row, so no entry a real doc carries is dropped.
      const assetData: unknown = assetDoc?.data;
      const assetArray: string[] = Array.isArray(assetData)
        ? assetData.filter((assetId): assetId is string => typeof assetId === "string")
        : [];
      if (action === "add") {
        if (!assetArray.includes(property)) assetArray.push(property);
      } else if (action === "remove") {
        const idx = assetArray.indexOf(property);
        if (idx >= 0) assetArray.splice(idx, 1);
      }

      const nextDoc: DynamicRow = {
        fileVersionId,
        nodeId,
        DOCUMENT_TYPE: DOCUMENT_TYPE.DOCUMENT_TYPE_ASSETDATA,
        data: assetArray,
      };

      return { fileVersionId, nodeId, docs: [...others, nextDoc] };
    });

    await replaceDynamicDocsForNodes(entries);
    dispatch(bumpDataVersion());
  };
}

type ModifiedModelPropsState = {
  // Discriminator — only "customMetadata" is dispatched today; the SearchSet1
  // grid effect keys off it to optimistically refresh the dataset.
  type: "customMetadata";
  nodeId: number[] | number;
  fileVersionId: string;
  fieldsData: {
    _id: string;
    value: string | number | boolean | string[] | null;
    selectedFormTemplate?: string;
  };
} | null;

const modifiedModelPropSlice = createSlice({
  name: "modifiedModelProps",
  initialState: null as ModifiedModelPropsState,
  reducers: {
    setModifiedModelProps: (_state: ModifiedModelPropsState, action: PayloadAction<ModifiedModelPropsState>) => {
      return action.payload;
    },
  },
});

/**
 * The `getDesignViewerSettings` payload reaches the reducer untyped (`@Actions/Viewer` reads it off
 * an untyped response body). `ViewerSetting` is a passthrough schema whose every declared field is
 * optional, so any plain object is a structurally valid settings bag; anything else — including a
 * missing response — resets the slice to `{}`.
 */
function isViewerSetting(value: unknown): value is ViewerSetting {
  return typeof value === "object" && value !== null;
}

const settingModalSlice = createSlice({
  name: "GetSettingModalData",
  initialState: {} as ViewerSetting,
  reducers: {
    setSettingModalAPIData: (_state: ViewerSetting, action: PayloadAction<unknown>) =>
      isViewerSetting(action.payload) ? action.payload : {},
    updateSettingModalAPIData: (state: ViewerSetting, action: PayloadAction<Partial<ViewerSetting>>) => ({
      ...state,
      ...action.payload,
    }),
  },
});

const bottomToolbarSlice = createSlice({
  name: "isBottomToolbarRefreshRequired",
  initialState: false,
  reducers: {
    setBottomToolbarRefresh: (_state: boolean, action: PayloadAction<boolean>) => action.payload,
  },
});

const transparentElementsSlice = createSlice({
  name: "transparentElements",
  initialState: [] as number[],
  reducers: {
    // Dispatched with HOOPS node-id arrays (`NodeId` is `number`), which is what the slice holds.
    updateTransparentElements: (_state: number[], action: PayloadAction<number[]>) => action.payload,
  },
});

interface AnnotationItem {
  id: string;
  type?: string;
  markup?: string;
  pageNumber?: number;
  subject?: string;
  contents?: string;
  color?: string;
  opacity?: number;
  rect?: number[];
  vertices?: number[];
  fontSize?: number;
  fontFamily?: string;
  strokeColor?: string;
  fillColor?: string;
  strokeWidth?: number;
  flags?: number;
  rotate?: number;
  createdAt?: number;
  updatedAt?: number;
  author?: string;
  userId?: string;
  issueId?: string;
  readOnly?: boolean;
  hidden?: boolean;
  customData?: Record<string, string | number | boolean | null>;

  /**
   * Geometry and style members written by the PDF annotation components through
   * `updateAnnotationProperties`. `AnnotationItem` is a local, weaker duplicate of `AnnotationData` in
   * `packages/helpers/types/pdf/types.ts`; every type below is taken from that union rather than invented:
   *   x/y/width/height/scaleX/scaleY/rotation/thickness  — BaseShapeAnnotation, TextAnnotation,
   *                                                        SignatureAnnotation, BaseStampAnnotation
   *   stroke/fill/fontColor/backgroundColor/borderColor  — `ColorObj`, NOT the `string` that
   *                                                        `strokeColor`/`fillColor` above declare
   *   points — `DistancePoints`/`AreaPoints`   groupPosition — `BaseAnnotation.groupPosition: Point`
   *   content/alignment/isBold/isItalic/isUnderline/isStrikeout — TextAnnotation
   */
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  scaleX?: number;
  scaleY?: number;
  rotation?: number;
  thickness?: number;
  points?: number[];
  groupPosition?: PdfPoint;
  stroke?: PdfColorObj;
  fill?: PdfColorObj;
  fontColor?: PdfColorObj;
  backgroundColor?: PdfColorObj;
  borderColor?: PdfColorObj;
  content?: string;
  alignment?: string;
  isBold?: boolean;
  isItalic?: boolean;
  isUnderline?: boolean;
  isStrikeout?: boolean;
}

/**
 * Both the annotation buckets on `ViewerStateShape` and `SetAnnotationsPayload.data` sit in `any`
 * slots (callers hand over `JSON.parse` output), so annotations are filtered into shape rather than
 * asserted: a non-object entry could never be read back — every consumer dereferences `.id`.
 */
function asAnnotations(items: unknown[]): AnnotationItem[] {
  return items.filter((item): item is AnnotationItem => typeof item === "object" && item !== null);
}

/** Reads one annotation bucket off the dynamic viewer state; a missing bucket reads as empty. */
function readAnnotationBucket(bucket: unknown): AnnotationItem[] {
  return Array.isArray(bucket) ? asAnnotations(bucket) : [];
}

/** Normalises `SetAnnotationsPayload.data`, which is either a list or a single annotation. */
function toAnnotationList(data: unknown): AnnotationItem[] {
  return asAnnotations(Array.isArray(data) ? data : [data]);
}

interface PageInfo {
  pageNumber: number;
  reason: string | null;
  zoomLevel: number;
  xScrollPosition: number;
  yScrollPosition: number;
}

export interface ViewerStateShape {
  isSidebarOpen: boolean;
  markupAnnotations: AnnotationItem[];
  nonMarkupAnnotations: AnnotationItem[];
  currentPageInfo: PageInfo;
  showAllIssueMarkups: boolean;
  reason: string;
  showObjectTreeShimmer: boolean;
  isPdfDocumentReady: boolean;
  isRotatedPdfPageInView: boolean;
  pdfCompare: {
    active: boolean;
    baseFileVersionId: string;
    revisionFileVersionId: string;
    initialMode?: CompareMode;
    initialPage?: number;
  } | null;
  // Allow additional dynamic viewer state fields (e.g. custom annotation types, feature flags)

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- handler slot / dynamic viewer state; see comment above
  [key: string]: any;
}

const DEFAULT_VIEWER_STATE: ViewerStateShape = {
  isSidebarOpen: true,
  markupAnnotations: [],
  nonMarkupAnnotations: [],
  currentPageInfo: {
    pageNumber: 1,
    reason: null,
    zoomLevel: 1,
    xScrollPosition: 0,
    yScrollPosition: 0,
  },
  showAllIssueMarkups: false,
  reason: "reset_viewer_state",
  showObjectTreeShimmer: true,
  isPdfDocumentReady: false,
  isRotatedPdfPageInView: false,
  pdfCompare: null,
  selectionMode: "element",
};

interface SetAnnotationsPayload {
  annotationType?: string;

  /**
   * A list of annotations, a single annotation, or whatever a caller's `JSON.parse` produced — the
   * bucket it lands in is one of the dynamic `[key: string]` slots below. `unknown` rather than
   * `any`: `addAnnotation` already normalises it through `toAnnotationList`, and `setAnnotations`
   * only forwards it into a slot the readers (`readAnnotationBucket`) filter into shape anyway, so
   * no reader here needs the value typed.
   */
  data: unknown;
  reason?: string;
}

interface DeleteAnnotationPayload {
  annotationType?: string;
  data: string;
  reason?: string;
}

interface UpdateViewerStatePayload {
  field: string;

  // `object` covers the remaining shapes callers push through this generic setter (plain records,
  // arrays, and their own named types); `Record<string, unknown>` would reject the named ones.
  value: string | number | boolean | null | AnnotationItem[] | AnnotationItem | PageInfo | object;
  reason?: string;
}

const viewerStateSlice = createSlice({
  name: "viewerState",
  initialState: DEFAULT_VIEWER_STATE,
  reducers: {
    resetViewerState: (state: ViewerStateShape) => ({ ...state, ...DEFAULT_VIEWER_STATE }),
    setPdfDocumentReady: (state: ViewerStateShape, action: PayloadAction<boolean>) => ({
      ...state,
      isPdfDocumentReady: action.payload,
    }),
    setRotatedPdfPageInView: (state: ViewerStateShape, action: PayloadAction<boolean>) => ({
      ...state,
      isRotatedPdfPageInView: action.payload,
    }),
    setViewerState: (state: ViewerStateShape, action: PayloadAction<Partial<ViewerStateShape>>) => {
      return {
        ...state,
        ...action.payload,
        reason: action.payload.reason ?? "",
      };
    },
    updateViewerState: (state: ViewerStateShape, action: PayloadAction<UpdateViewerStatePayload>) => ({
      ...state,
      [action.payload.field]: action.payload.value,
      reason: action.payload.reason ?? "",
    }),
    setAnnotations: (state: ViewerStateShape, action: PayloadAction<SetAnnotationsPayload>) => {
      const annotationType = action.payload.annotationType || "nonMarkupAnnotations";
      return {
        ...state,
        [annotationType]: action.payload.data,
        reason: action.payload.reason ?? "",
      };
    },
    deleteAnnotation: (state: ViewerStateShape, action: PayloadAction<DeleteAnnotationPayload>) => {
      const annotationType = action.payload.annotationType || "nonMarkupAnnotations";
      const existing = readAnnotationBucket(state[annotationType]);
      return {
        ...state,
        [annotationType]: existing.filter((annotation: AnnotationItem) => annotation.id !== action.payload.data),
        reason: action.payload.reason ?? "",
      };
    },
    addAnnotation: (state: ViewerStateShape, action: PayloadAction<SetAnnotationsPayload>) => {
      const annotationType = action.payload.annotationType || "nonMarkupAnnotations";
      const dataToAdd = toAnnotationList(action.payload.data);
      const existing = readAnnotationBucket(state[annotationType]);
      return {
        ...state,
        [annotationType]: [...existing, ...dataToAdd],
        reason: action.payload.reason ?? "",
      };
    },
    updateAnnotationProperties: (
      state: ViewerStateShape,

      action: PayloadAction<{
        annotationType?: string;
        /** Partial annotation fields, spread over the matching entry below.
         *  Written as an explicitly-undefined-tolerant partial rather than `Partial<AnnotationItem>`:
         *  callers deliberately hand over keys holding `undefined` (e.g. a shape with no fill), and
         *  the spread below must keep clearing the field, so the key may not simply be omitted. */
        data: { id: string; properties: { [K in keyof AnnotationItem]?: AnnotationItem[K] | undefined } };
        reason?: string;
      }>,
    ) => {
      const annotationType = action.payload.annotationType || "nonMarkupAnnotations";
      const { id, properties } = action.payload.data;
      const existing = readAnnotationBucket(state[annotationType]);
      const targetIndex = existing.findIndex((annotation: AnnotationItem) => annotation.id === id);
      if (targetIndex === -1) return state;
      const targetAnnotation = existing[targetIndex]!;
      const hasChanges = Object.entries(properties).some(([key, value]) => {
        // `key` comes from `Object.entries(properties)`, so it is a plain `string`, not a `keyof`;
        // `Reflect.get` is the dynamic read that does not require pretending otherwise.
        const current: unknown = Reflect.get(targetAnnotation, key);
        return !_.isEqual(current, value);
      });
      if (!hasChanges) {
        return {
          ...state,
          reason: "unchanged_annotations",
        };
      }
      const newAnnotations = [...existing];
      newAnnotations[targetIndex] = { id, ...newAnnotations[targetIndex], ...properties } as AnnotationItem;
      return {
        ...state,
        [annotationType]: newAnnotations,
        reason: action.payload.reason ?? "",
      };
    },
    setCurrentPagePDF: (state: ViewerStateShape, action: PayloadAction<PageInfo>) => ({
      ...state,
      currentPageInfo: action.payload,
    }),
    setShowObjectTreeShimmer: (state: ViewerStateShape, action: PayloadAction<boolean>) => ({
      ...state,
      showObjectTreeShimmer: action.payload,
    }),
  },
});

export interface NestedMeasurementEntry {
  originalValue: number;
  alteredValue: number;
  type: string;
  name: string;
}

export interface ParsedNestedMeasurements {
  data: NestedMeasurementEntry[];
  total: NestedMeasurementEntry;
}

/**
 * The measurement grid row parked in redux while the nested-measurements modal is open. Ids are indexed out
 * of the server `MeasurementItem` so their branding survives — except `measurementId`, which is a
 * client-minted UUID business key, not an ObjectId.
 *
 * Every member is `| undefined` as well as optional: the record is copied straight off a grid row whose
 * fields are all `.optional()` on `measurementItemSchema`, so under exactOptionalPropertyTypes the writers
 * hand over explicit `undefined`s rather than omitting keys.
 */
export interface SelectedMeasurementRecord {
  measurementId?: string | undefined;
  name?: string | undefined;
  type?: string | undefined;
  quantity?: number | undefined;
  scale?: string | undefined;
  markup?: string | undefined;
  takeOffItemId?: NonNullable<MeasurementItem["takeOffItemId"]> | undefined;
  fileVersionId?: MeasurementItem["fileVersionId"] | undefined;
  estimateId?: MeasurementItem["estimateId"] | undefined;
  nestedMeasurements?:
    Array<{ measurementId: string; quantity: number }> | string | ParsedNestedMeasurements | null | undefined;
  referenceMeasurements?: ReferenceMeasurement[] | null | undefined;
  formula?: string | null | undefined;
  manualUnit?: string | null | undefined;
  // `null` is a stored value, not an absence: `measurementItemSchema.isManualMeasurement` is
  // `.nullable()` because ~74k Measurements rows hold an explicit null, and the grid row is copied in.
  isManualMeasurement?: boolean | null | undefined;
  sheetId?: number | undefined;
}

const selectedNestedMeasurementsSlice = createSlice({
  name: "getSelectedNestedMeasurements",
  initialState: {} as SelectedMeasurementRecord,
  reducers: {
    setSelectedNestedMeasurements: (
      _state: SelectedMeasurementRecord,
      action: PayloadAction<SelectedMeasurementRecord>,
    ) => action.payload,
  },
});

// `any[]` LEFT IN PLACE, and it is load-bearing: this is a handler slot, and the measurement callbacks
// stored here declare their own concrete parameters. Under `strictFunctionTypes` a
// `(...args: unknown[]) => void` slot rejects every one of them.
// RE-ATTEMPTED. Method shorthand is not applicable — this is a standalone type alias used as a VALUE type,
// so there is no member syntax to make bivariant. The rest-param form `(...args: never[]) => void` would
// accept every handler on the way IN, but it is then handed to `Viewer.tsx:2417`'s `callback` prop, whose
// own declared signature would have to accept it. That consumer is outside this module.
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- handler slot / dynamic viewer state; see comment above
type MeasurementCallback = ((...args: any[]) => void) | null | undefined;

// `object`, not a keyed record: the single consumer (Viewer.tsx) already asserts this to its own
// `MeasurementAttributeStructure`, and a named type is assignable to `object` but not to
// `Record<string, unknown>`.
type MeasurementStructure = object | null | undefined;

export interface MeasurementAttributePopupState {
  show: boolean;
  callback: MeasurementCallback;
  structure: MeasurementStructure;
}

const measurementAttributePopupSlice = createSlice({
  name: "measurementAttributePopup",
  initialState: { show: false, callback: null, structure: null } as MeasurementAttributePopupState,
  reducers: {
    setMeasurementAttributePopup: (
      _state: MeasurementAttributePopupState,
      action: PayloadAction<MeasurementAttributePopupState>,
    ) => ({ ...action.payload }),
  },
});

/** Subset of ViewRecord stored in reducer: the id and a deserialized viewpoint object. */
type DefaultViewState = Pick<ViewRecord, "_id"> & {
  viewpoint?: Record<string, string | number | boolean | null | Record<string, string | number | boolean | null>>;
};

const viewSlice = createSlice({
  name: "viewReducer",
  initialState: null as DefaultViewState | null,
  reducers: {
    setDefaultView: (_state: DefaultViewState | null, action: PayloadAction<DefaultViewState | null>) => action.payload,
  },
});

const scsFileLoadProgressSlice = createSlice({
  name: "scsFileLoadProgress",
  initialState: { isLoading: false, progress: 0 },
  reducers: {
    setScsFileLoadProgress: (
      state: { isLoading: boolean; progress: number },
      action: PayloadAction<{ isLoading: boolean; progress: number }>,
    ) => ({
      ...state,
      ...action.payload,
    }),
    resetScsFileLoadProgress: () => ({ isLoading: false, progress: 0 }),
  },
});

export const { setModalStatus, setShowOnlyModal, setAnimationForIssueCreation } = hoopsModalSlice.actions;
export const { setIsSheetActive } = sheetActiveSlice.actions;
export const { setFaceSelection } = faceSelectionSlice.actions;
export const { setSocketDisconnected } = socketDisconnectedSlice.actions;
export const { setModelProperties, bumpDataVersion } = modelPropertiesSlice.actions;
export const { setSettingModalAPIData, updateSettingModalAPIData } = settingModalSlice.actions;
export const { setModifiedModelProps } = modifiedModelPropSlice.actions;
export const { setBottomToolbarRefresh } = bottomToolbarSlice.actions;
export const { updateTransparentElements } = transparentElementsSlice.actions;
export const {
  resetViewerState,
  setViewerState,
  setPdfDocumentReady,
  setRotatedPdfPageInView,
  updateViewerState,
  setAnnotations,
  deleteAnnotation,
  addAnnotation,
  updateAnnotationProperties,
  setCurrentPagePDF,
  setShowObjectTreeShimmer,
} = viewerStateSlice.actions;
export const { setSelectedNestedMeasurements } = selectedNestedMeasurementsSlice.actions;
export const { setMeasurementAttributePopup } = measurementAttributePopupSlice.actions;
export const { setDefaultView } = viewSlice.actions;
export const { setScsFileLoadProgress, resetScsFileLoadProgress } = scsFileLoadProgressSlice.actions;

export const HoopsModalStatus = hoopsModalSlice.reducer;
export const isSheetActive = sheetActiveSlice.reducer;
export const faceSelection = faceSelectionSlice.reducer;
export const socketDisconnected = socketDisconnectedSlice.reducer;
export const modelProperties = modelPropertiesSlice.reducer;
export const GetSettingModalData = settingModalSlice.reducer;
export const isBottomToolbarRefreshRequired = bottomToolbarSlice.reducer;
export const transparentElements = transparentElementsSlice.reducer;
export const viewerState = viewerStateSlice.reducer;
export const getSelectedNestedMeasurements = selectedNestedMeasurementsSlice.reducer;
export const measurementAttributePopup = measurementAttributePopupSlice.reducer;
export const viewReducer = viewSlice.reducer;
export const modifiedModelProps = modifiedModelPropSlice.reducer;
export const scsFileLoadProgress = scsFileLoadProgressSlice.reducer;
